/**
 * 狐小E 客户端认证信息（网关中转请求需要）
 *
 * 中转接口 gateway.json / gateway-v2.json 要求把客户端认证信息拼进 query 参数，
 * 本模块负责通过原生桥一次性拿到 sid / uk 并缓存，供 src/api/request.ts 使用。
 *
 * 三个不变量：
 * 1. 全页面生命周期内所有调用方共享同一个 pending Promise（并发去重）
 * 2. 结果（含失败）永久缓存，不重试
 * 3. 任何失败都 resolve(null)，永不 reject —— 调用方不需要 try/catch
 *
 * 「一次性」的准确语义是每个页面生命周期一次：整页 reload（request.ts 的 30x
 * 跳转、usePageVisibility 的 refreshApp）会重置模块状态并重新取一次，这是正确的
 * —— reload 后 sid / uk 可能已经变化。
 */
import { addBreadcrumb } from '@/utils/sentry'
import type { HxeGetAuthenticationInfoResponse } from '@/typings/hxe'

/** 只关心中转需要的两个字段 */
export type AuthInfo = Pick<HxeGetAuthenticationInfoResponse, 'sid' | 'uk'>

/**
 * 等待 finish 回调的超时时间(ms)。
 *
 * 文档中该接口只有 finish、没有 fail 回调，不设超时会让首个网关请求永久挂起。
 * 由于不重试，超时是唯一一次机会，故取值偏宽松（桥是本地调用，正常 < 50ms 返回）；
 * 超时只影响首个请求，代价有界，而「整个会话拿不到 sid / uk」严重得多。
 */
export const AUTH_INFO_TIMEOUT = 3000

/** query 中的字段顺序，固定下来便于测试断言与排查 */
const AUTH_FIELDS = ['sid', 'uk'] as const

/** 桥调用结果缓存；resolve 为 null 表示已失败并永久降级 */
let authInfoPromise: Promise<AuthInfo | null> | null = null

/** 超时后晚到的回调结果，用于补救后续请求（不是重试，不会再调一次桥） */
let lateAuthInfo: AuthInfo | null = null

/** 清洗桥返回值：只保留非空的 sid / uk，两者皆空视为无效 */
function pickAuthInfo(raw: unknown): AuthInfo | null {
  if (!raw || typeof raw !== 'object') return null
  const source = raw as Record<string, unknown>
  const result = { sid: '', uk: '' } as AuthInfo
  let hasValue = false
  for (const key of AUTH_FIELDS) {
    const value = source[key]
    const text =
      typeof value === 'string' ? value : value == null ? '' : String(value)
    result[key] = text
    if (text) hasValue = true
  }
  return hasValue ? result : null
}

/** 调用原生桥取认证信息。永不 reject，任何失败一律 resolve(null) */
function callBridge(): Promise<AuthInfo | null> {
  return new Promise<AuthInfo | null>(resolve => {
    let settled = false
    let timedOut = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const settle = (value: AuthInfo | null) => {
      if (settled) return
      settled = true
      if (timer) clearTimeout(timer)
      resolve(value)
    }

    // 官方文档为小写方法名，部分客户端版本只暴露大写写法
    const bridge = window.hxe as any
    const fn: ((options: any) => void) | undefined =
      bridge?.getAuthenticationInfo ?? bridge?.GetAuthenticationInfo

    if (typeof fn !== 'function') {
      addBreadcrumb({
        message: '客户端未提供 getAuthenticationInfo，降级不带认证参数',
        category: 'app/auth-info',
        level: 'warning'
      })
      settle(null)
      return
    }

    timer = setTimeout(() => {
      timedOut = true
      addBreadcrumb({
        message: 'hxe.getAuthenticationInfo 回调超时，降级不带认证参数',
        category: 'app/auth-info',
        level: 'warning',
        data: { timeout: AUTH_INFO_TIMEOUT }
      })
      settle(null)
    }, AUTH_INFO_TIMEOUT)

    try {
      fn.call(bridge, {
        finish(res: unknown) {
          const info = pickAuthInfo(res)
          if (timedOut) {
            // 迟到回调：本次请求已经降级发出，但结果留给后续请求使用
            if (info) {
              lateAuthInfo = info
              addBreadcrumb({
                message: 'hxe.getAuthenticationInfo 回调迟到，结果已缓存',
                category: 'app/auth-info',
                level: 'info'
              })
            }
            return
          }
          settle(info)
        }
      })
    } catch (error) {
      addBreadcrumb({
        message: 'hxe.getAuthenticationInfo 调用同步抛错，降级不带认证参数',
        category: 'app/auth-info',
        level: 'warning',
        data: { errorMessage: (error as Error)?.message }
      })
      settle(null)
    }
  })
}

/**
 * 获取客户端认证信息（一次性，并发去重）
 * 永不 reject；拿不到时返回 null，由调用方按「不带参数继续请求」降级处理。
 */
export function getAuthInfo(): Promise<AuthInfo | null> {
  if (lateAuthInfo) return Promise.resolve(lateAuthInfo)
  authInfoPromise ??= callBridge()
  return authInfoPromise
}

/** 构建中转接口的 query 片段（不含起始分隔符），无可用字段时返回 '' */
export function buildAuthQuery(auth: AuthInfo | null): string {
  if (!auth) return ''
  return AUTH_FIELDS.filter(key => Boolean(auth[key]))
    .map(key => `${key}=${encodeURIComponent(auth[key])}`)
    .join('&')
}

/** 仅测试用：重置模块级缓存 */
export function resetAuthInfo(): void {
  authInfoPromise = null
  lateAuthInfo = null
}
